import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ExamTarget } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useExams, useMe } from '../lib/queries';
import { ErrorNotice } from '../components/ui';
import { CountrySelect } from '../components/CountrySelect';
import { browserTimezone, guessCountry } from '../lib/locale';

const REFERRAL_SOURCES = [
  'Google or another search engine',
  'Facebook or Instagram',
  'A friend or colleague',
  'A pharmacy forum or WhatsApp group',
  'My university or bridging course',
  'Somewhere else',
];

const TARGETS: { value: ExamTarget; label: string; body: string }[] = [
  { value: 'CAOP', label: 'CAOP', body: 'Competency Assessment of Overseas Pharmacists — restricted open-book, 70 questions in 120 minutes.' },
  { value: 'OPRA', label: 'OPRA', body: 'Overseas Pharmacist Readiness Assessment — closed-book, 120 questions in 150 minutes.' },
  { value: 'BOTH', label: 'Both', body: 'Preparing for both. You will get a separate baseline and progress dashboard for each.' },
];

export function Onboarding() {
  const [target, setTarget] = useState<ExamTarget>('CAOP');
  const [examDate, setExamDate] = useState('');
  const [hours, setHours] = useState('');
  const [priorAttempt, setPriorAttempt] = useState<'' | 'first' | 'resit'>('');
  const [referralSource, setReferralSource] = useState('');
  // Lazily seeded from the browser locale so the value in state always
  // matches what the picker is showing.
  const [country, setCountry] = useState(guessCountry);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  useExams();

  // Google sign-ups never see the registration form, so this is where they are
  // asked for the two things Google does not supply.
  const { data: me } = useMe();
  const needsCountry = me?.user ? !me.user.country : false;
  const needsMarketing = me?.user ? !me.user.marketingAsked : false;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.patch('/api/account/onboarding', {
        examTarget: target,
        // These fields are genuinely optional — the planner applies sensible
        // defaults rather than blocking.
        examDate: examDate || null,
        weeklyStudyHours: hours ? Number(hours) : null,
        priorAttempt: priorAttempt === '' ? null : priorAttempt === 'resit',
        referralSource: referralSource || null,
        ...(needsCountry ? { country } : {}),
        ...(needsMarketing ? { marketingOptIn } : {}),
        ...(browserTimezone() ? { timezone: browserTimezone() } : {}),
      });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save your answers.');
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl py-4">
      <h1 className="text-2xl font-bold tracking-tight text-ink">Let&apos;s set up your study plan</h1>
      <p className="mt-2 text-muted">A few quick questions. You can change any of them later.</p>

      <form onSubmit={submit} className="card mt-6 space-y-6 p-4 sm:p-6">
        {error ? <ErrorNotice message={error} /> : null}

        {needsCountry ? (
          <div>
            <label className="label" htmlFor="ocountry">Which country are you in?</label>
            <CountrySelect id="ocountry" required value={country} onChange={setCountry} />
            <p className="mt-1.5 text-xs text-muted">Used to set your currency and time zone.</p>
          </div>
        ) : null}

        <fieldset>
          <legend className="label">Which exam are you preparing for?</legend>
          <div className="space-y-2.5">
            {TARGETS.map((t) => (
              <label
                key={t.value}
                className={`flex cursor-pointer gap-3 rounded-lg border p-4 transition ${
                  target === t.value ? 'border-brand-600 bg-brand-50 dark:bg-brand-500/15 ring-1 ring-brand-600' : 'border-line hover:bg-raised'
                }`}
              >
                <input
                  type="radio"
                  name="target"
                  className="mt-1 h-4 w-4 accent-brand-700"
                  checked={target === t.value}
                  onChange={() => setTarget(t.value)}
                />
                <span>
                  <span className="block text-sm font-semibold text-ink">{t.label}</span>
                  <span className="mt-0.5 block text-sm text-muted">{t.body}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="label" htmlFor="examDate">
            When is your exam? <span className="font-normal text-subtle">(optional)</span>
          </label>
          <input
            id="examDate"
            type="date"
            className="input"
            value={examDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setExamDate(e.target.value)}
          />
          <p className="mt-1.5 text-xs text-muted">
            Used to size your revision timetable. Skip it and we will plan eight weeks from now.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="hours">
            How many hours a week can you study? <span className="font-normal text-subtle">(optional)</span>
          </label>
          <input
            id="hours"
            type="number"
            min={1}
            max={80}
            className="input"
            placeholder="8"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
          <p className="mt-1.5 text-xs text-muted">
            Be realistic — a plan you cannot follow is worse than no plan. We default to 8 hours.
          </p>
        </div>

        <fieldset>
          <legend className="label">
            Have you sat this exam before? <span className="font-normal text-subtle">(optional)</span>
          </legend>
          <div className="flex flex-col gap-3 sm:flex-row">
            {([
              ['first', 'This is my first attempt'],
              ['resit', 'I am resitting'],
            ] as const).map(([value, label]) => (
              <label
                key={value}
                className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition ${
                  priorAttempt === value ? 'border-brand-600 ring-1 ring-brand-600' : 'border-line hover:bg-raised'
                }`}
              >
                <input
                  type="radio"
                  name="priorAttempt"
                  className="h-4 w-4 accent-brand-700"
                  checked={priorAttempt === value}
                  onChange={() => setPriorAttempt(value)}
                />
                <span className="text-ink">{label}</span>
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted">
            Resitters get a plan weighted towards their weakest domains from the start.
          </p>
        </fieldset>

        <div>
          <label className="label" htmlFor="referral">
            How did you hear about us? <span className="font-normal text-subtle">(optional)</span>
          </label>
          <select id="referral" className="input" value={referralSource} onChange={(e) => setReferralSource(e.target.value)}>
            <option value="">Prefer not to say</option>
            {REFERRAL_SOURCES.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        {needsMarketing ? (
          <label className="flex cursor-pointer gap-3 rounded-lg border border-line p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-700"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
            />
            <span className="text-sm text-muted">
              Email me study tips and product updates. You can unsubscribe at any time. Account and
              billing emails are sent regardless.
            </span>
          </label>
        ) : null}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Saving…' : 'Continue to my dashboard'}
        </button>
      </form>
    </div>
  );
}
